<template>
  <div class="heatmap-pagination">
    <SvgDisplay
      src="Icon-Chevron-Left_x16"
      class="arrow arrow-left"
      role="button"
      :class="{ 'arrow-disabled': !canStepLeft }"
      :aria-label="ariaLabels[locale].stepBackwards"
      :tabindex="canStepLeft ? 0 : -1"
      @click="stepLeft"
      @keydown.enter="stepLeft"
      @keydown.space="(event: Event) => handleSpaceKey(event, 'stepLeft')"
    ></SvgDisplay>
    <TextBase :text="pageTitle" type="label" weight="bold" textWrap="nowrap" />
    <SvgDisplay
      src="Icon-Chevron-Right_x16"
      class="arrow arrow-right"
      role="button"
      :class="{ 'arrow-disabled': !canStepRight }"
      :aria-label="ariaLabels[locale].stepForwards"
      :tabindex="canStepRight ? 0 : -1"
      @click="stepRight"
      @keydown.enter="stepRight"
      @keydown.space="(event: Event) => handleSpaceKey(event, 'stepRight')"
    ></SvgDisplay>
  </div>
</template>
<script lang="ts" setup>
import TextBase from './TextBase.vue'
import SvgDisplay from './SvgDisplay.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

type Props = {
  totalItems: number
  itemsPerPage: number
  currentPage: number
  pageTitle?: string
}

const ariaLabels = {
  en: {
    stepBackwards: 'Step Backwards',
    stepForwards: 'Step Forwards'
  },
  sv: {
    stepBackwards: 'Stega bakåt',
    stepForwards: 'Stega framåt'
  }
}

const { locale } = useI18n()
const props = defineProps<Props>()
const emits = defineEmits(['stepLeft', 'stepRight'])

const canStepLeft = computed(() => {
  if (props.itemsPerPage === 0 || props.totalItems === 0) {
    return false
  }
  if (props.currentPage === 0) {
    return false
  }
  return true
})

const canStepRight = computed(() => {
  if (props.itemsPerPage === 0 || props.totalItems === 0) {
    return false
  }
  return props.currentPage < Math.ceil(props.totalItems / props.itemsPerPage) - 1
})

function handleSpaceKey(event: Event, direction: 'stepLeft' | 'stepRight') {
  event.preventDefault() // Prevent space bar scrolling
  if (direction === 'stepLeft') {
    stepLeft()
  } else if (direction === 'stepRight') {
    stepRight()
  }
}

function stepLeft() {
  if (canStepLeft.value) {
    emits('stepLeft')
  }
}

function stepRight() {
  if (canStepRight.value) {
    emits('stepRight')
  }
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
.heatmap-pagination {
  height: 24px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
}

.arrow {
  height: 24px;
  width: 24px;
  justify-content: center;
  align-items: center;
  flex-shrink: 1;
  color: variables.$color-primary;
  cursor: pointer;
  &.arrow-disabled {
    cursor: default;
    color: variables.$color-disabled;
  }
}
</style>
